import { Button, Col, Form, FormGroup, FormSelect, Row } from "react-bootstrap"
import { useCallback, useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { MessageType, useMessageReport } from "../../components/MessageReporter"
import { ApiClient } from "../../ApiClient"
import { InfoList } from "../../components/InfoList"
import { formatDateTime } from "../../util/formatDataTime"
import { MapTenantToTenantName } from "./UserAdd"
import { useRole } from "../../components/RoleContext"
import { useApi } from "../../hooks/ApiProvider"

export const UserDetail = () => {
    const { id } = useParams()
    const { getAccessTokenSilently } = useAuth0()
    const { addMessage } = useMessageReport()

    const [user, setUser] = useState()
    const [role, setRole] = useState()
    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false)
    const { permissions: userPermissions } = useRole()

    const isDisabled = (r) => {
        if (!r || !r.permissions || !userPermissions) {
            return true
        }

        return !r.permissions.every((perm) => userPermissions.includes(perm))
    }

    const [roles, setRoles] = useState([])

    const { managementApi, consumerApi } = useApi()

    useEffect(() => {
        managementApi
            .managementGetRoles()
            .then((roles) => setRoles(roles))
            .catch((e) => {
                addMessage(
                    "Failed to get roles",
                    "Something went wrong while getting the roles, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }, [managementApi, addMessage])

    useEffect(() => {
        getAccessTokenSilently()
            .then((token) =>
                ApiClient.get("/v2/consumer/users/" + id, {
                    Authorization: "Bearer " + token,
                })
            )
            .then(({ data: user }) => {
                setUser(user)
                setRole(user.role_properties)
            })
            .catch((e) => {
                addMessage(
                    "Failed to get user",
                    "Something went wrong while getting the user, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
    }, [getAccessTokenSilently, addMessage, id])

    const updateUser = useCallback(() => {
        setUpdateButtonDisabled(true)

        consumerApi
            .updateUser({
                userId: user.id,
                updateData: {
                    roleProperties: role,
                },
            })
            .then(() => {
                addMessage("User updated", "Successfully updated user", MessageType.success)
            })
            .catch((e) => {
                addMessage(
                    "Failed to update user",
                    "Something went wrong while updating the user, " +
                        "see the console for more details.",
                    MessageType.error,
                    e
                )
            })
            .finally(() => setUpdateButtonDisabled(false))
    }, [role, consumerApi, addMessage, user])

    return (
        <Row className="content-section">
            <Col>
                <h2>User information</h2>
                <Link
                    className={
                        "btn btn-success float-end mb-3" + (isDisabled(role) ? " disabled" : "")
                    }
                    to={`/user/${id}/permissions`}
                >
                    Customize user permissions
                </Link>
                {user && (
                    <>
                        <InfoList>
                            <InfoList.Item name="Id" value={user.id} />
                            <InfoList.Item name="Email" value={user.email} />
                            <InfoList.Item
                                name="Tenant"
                                value={MapTenantToTenantName(user.tenant)}
                            />
                            <InfoList.Item
                                name="Last activity"
                                value={formatDateTime(user.last_auth)}
                            />
                        </InfoList>
                        <FormGroup>
                            <Form.Label>Role</Form.Label>
                            <FormSelect
                                disabled={isDisabled(role)}
                                value={role.name}
                                id="role-select"
                                onChange={(e) => {
                                    setRole(roles.find((r) => r.name === e.target.value))
                                }}
                            >
                                {roles.map((r) => (
                                    <option key={r.name} value={r.name} disabled={isDisabled(r)}>
                                        {r.name.toLowerCase()}
                                    </option>
                                ))}
                            </FormSelect>
                        </FormGroup>
                        <Button
                            className="mt-4"
                            disabled={updateButtonDisabled || isDisabled(role)}
                            onClick={updateUser}
                        >
                            Update
                        </Button>
                    </>
                )}
            </Col>
        </Row>
    )
}
